import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Container,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";

import {
  Phone,
  TrendingUp,
  PieChart,
  Trash2,
  PlusCircle,
  RefreshCw,
  Send,
  User,
} from "react-feather";
import { FaWallet as Wallet } from "react-icons/fa";
import Swal from "sweetalert2";

import FinoDmtService from "../../../services/FinoDmtService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";

const SenderHome = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { customerMobile, data } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [beneficiaryList, setBeneficiaryList] = useState([]);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [deleteToken, setDeleteToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState(null);

  // Fetch beneficiary list on component mount
  useEffect(() => {
    if (customerMobile) {
      fetchBeneList();
    }
  }, [customerMobile]);

  const fetchBeneList = async () => {
    if (loading) return;

    setLoading(true);

    try {
      const result = await FinoDmtService.beneList(customerMobile);

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setBeneficiaryList(res?.data?.beneficiaries || []);
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteBeneHandler = async (beneficiary) => {
    if (loading) return;

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this beneficiary.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setSelectedBeneficiary(beneficiary);
    setLoading(true);

    try {
      const result = await FinoDmtService.deleteBene(
        customerMobile,
        beneficiary.bene_id,
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        await fetchBeneList();
        Swal.fire("Deleted!", res.message, "success");
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyBeneficiary = async (bene) => {
    const result = await Swal.fire({
      title: "Verify Beneficiary",
      html: `<p>You are about to verify:</p>
               <p><strong>Name:</strong> ${bene.bene_name}<br>
               <strong>Account:</strong> ${bene.account_no}</p>
               <p class="text-danger"><i class="fas fa-info-circle"></i> ₹4.00 will be debited from your wallet for verification charges.</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Proceed",
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    try {
      const result = await FinoDmtService.pennyDrop(
        customerMobile,
        bene.bene_name,
        bene.bene_id,
        bene.bank_id,
        bene.account_no,
        bene.ifsc_code,
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        const respData = res.data;
        Swal.fire({
          title: "Verification Successful",
          html: `<p>Beneficiary <strong>${respData?.bene_name}</strong> verified.</p>
                   <p>UTR: <strong>${respData?.utr}</strong></p>`,
          icon: "success",
        });
        fetchBeneList();
      } else {
        Swal.fire("Oops!", res.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddBeneficiary = () => {
    navigate("/fino-money-transfer/add-beneficiary", {
      state: { customerMobile, data },
    });
  };

  const handleFundTransfer = (beneficiary) => {
    navigate("/fino-money-transfer/fund-transfer", {
      state: {
        customerMobile,
        data,
        beneficiary,
      },
    });
  };

  const handleRefresh = () => {
    fetchBeneList();
  };

  const totalLimit = 300000;

  const available = Number(data?.limit) || 0;

  // consumed = total - available
  const consumed = totalLimit - available;

  // prevent negative values
  const safeConsumed = Math.max(consumed, 0);
  const safeAvailable = Math.max(available, 0);

  const total = totalLimit;

  // percentage calculation
  const percentage = total > 0 ? (safeConsumed / total) * 100 : 0;

  // cap at 100%
  const safePercentage = Math.min(percentage, 100);

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          {/* Sender Information Card */}
          <Card className="shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <div>
                  <h4 className="mb-1">Fino Money Transfer</h4>
                </div>
              </div>
              {/* Powered by Fino Bank Logo */}
              <div className="text-end">
                <img
                  src="/assets/images/fino-bank-logo.png"
                  alt="fino-logo"
                  height={20}
                />
              </div>
            </div>

            {/* Sender Information - Compact Horizontal Layout */}
            <Row className="mb-3">
              <Col md={8}>
                <div className="d-flex flex-column flex-md-row gap-3">
                  {/* Wallet Holder Card */}
                  {/* <div
                    className="bg-light rounded p-3 d-flex align-items-center"
                    style={{ flex: 1 }}
                  >
                    <div className="bg-primary rounded-circle p-2 me-3">
                      <User size={20} className="text-white" />
                    </div>
                    <div>
                      <small className="text-muted d-block">
                        Wallet Holder
                      </small>
                      <strong className="text-dark">
                        {data?.wallet_holder_name || "N/A"}
                      </strong>
                    </div>
                  </div> */}

                  {/* Mobile Card */}
                  <div
                    className="bg-light rounded p-3 d-flex align-items-center"
                    style={{ flex: 1 }}
                  >
                    <div className="bg-info rounded-circle p-2 me-3">
                      <Phone size={20} className="text-white" />
                    </div>
                    <div>
                      <small className="text-muted d-block">
                        Customer Mobile Number
                      </small>
                      <strong className="text-dark">
                        {customerMobile || "N/A"}
                      </strong>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Limits Section - More Compact Design */}
            <div className="mb-1">
              <h6 className="text-muted mb-3">
                Money Transfer Limits (Monthly)
              </h6>
              <Row>
                <Col md={4} className="mb-3">
                  <div className="border-start border-3 border-success p-3 bg-light rounded-end">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted d-block">
                          Limit Consumed
                        </small>
                        <h5 className="text-success mb-0">₹{safeConsumed}</h5>
                      </div>
                      <div className="bg-success rounded-circle p-2">
                        <TrendingUp size={18} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={4} className="mb-3">
                  <div className="border-start border-3 border-primary p-3 bg-light rounded-end">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted d-block">
                          Available Limit
                        </small>
                        <h5 className="text-primary mb-0">₹{safeAvailable}</h5>
                      </div>
                      <div className="bg-primary rounded-circle p-2">
                        <Wallet size={18} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={4} className="mb-3">
                  <div className="border-start border-3 border-info p-3 bg-light rounded-end">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted d-block">
                          Total Limit
                        </small>
                        <h5 className="text-info mb-0">₹300000</h5>
                      </div>
                      <div className="bg-info rounded-circle p-2">
                        <PieChart size={18} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            {/* Progress Bar for Visual Representation */}
            <div>
              <div className="d-flex justify-content-between mb-2">
                <small className="text-muted">Limit Usage</small>
                <small className="text-muted">
                  {safePercentage.toFixed(1)}%
                </small>
              </div>

              <div className="progress" style={{ height: "8px" }}>
                <div
                  className={`progress-bar ${
                    safePercentage > 80
                      ? "bg-danger"
                      : safePercentage > 50
                        ? "bg-warning"
                        : "bg-success"
                  }`}
                  role="progressbar"
                  style={{
                    width: `${safePercentage}%`,
                    transition: "width 0.5s ease",
                  }}
                  aria-valuenow={safePercentage}
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
            </div>
          </Card>
          {/* Beneficiary List Card */}
          <Card className="shadow-sm p-3 mb-3" style={{ borderRadius: "12px" }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0">Beneficiaries</h5>
              <div>
                <Button
                  variant="outline-primary"
                  className="me-2"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshCw size={18} /> Refresh
                </Button>
                <Button
                  variant="success"
                  onClick={handleAddBeneficiary}
                  disabled={loading}
                >
                  <PlusCircle size={18} /> Add Beneficiary
                </Button>
              </div>
            </div>

            {beneficiaryList.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No beneficiaries found</p>
                <Button variant="primary" onClick={handleAddBeneficiary}>
                  <PlusCircle size={18} /> Add Your First Beneficiary
                </Button>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Bank</th>
                      <th>Account Number</th>
                      <th>IFSC</th>
                      <th>Account Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiaryList.map((beneficiary, index) => (
                      <tr key={beneficiary.bene_id}>
                        <td>{index + 1}</td>
                        <td>{beneficiary.bene_name}</td>
                        <td>{beneficiary.bank_name}</td>
                        <td>{beneficiary.account_no}</td>
                        <td>{beneficiary.ifsc_code}</td>
                        <td>
                          {beneficiary.verified === "1" ? (
                            <Badge bg="success">Verified</Badge>
                          ) : (
                            <Badge bg="warning">Not Verified</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleFundTransfer(beneficiary)}
                            >
                              <Send size={16} /> Transfer
                            </Button>

                            {beneficiary.verified !== "1" && (
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => verifyBeneficiary(beneficiary)}
                                title="Verify Beneficiary"
                              >
                                <i className="fas fa-check"></i> Verify
                              </Button>
                            )}

                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => deleteBeneHandler(beneficiary)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SenderHome;
